import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  MapPin, Loader2, CheckCircle, Home, DollarSign, 
  TrendingUp, Building2, Calendar, Sparkles, Crown, Lock
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import { useNavigate } from 'react-router-dom';

export default function AddressAutofill({ token, onDataFetched, initialAddress = '', userTier = 'light' }) {
  const navigate = useNavigate();
  const [address, setAddress] = useState(initialAddress);
  const [loading, setLoading] = useState(false);
  const [marketData, setMarketData] = useState(null);

  const isPro = userTier === 'pro' || userTier === 'developer';

  // Detect if address is Canadian
  const detectCountry = (addr) => {
    const addrUpper = addr.toUpperCase();
    const canadianPostal = /[A-Z]\d[A-Z]\s?\d[A-Z]\d/.test(addrUpper);
    if (canadianPostal) return 'CA';
    
    const canadianProvinces = [' ON,', ' ON ', ' BC,', ' BC ', ' AB,', ' AB ', ' QC,', ' QC ', 
                               ' MB,', ' MB ', ' SK,', ' SK ', ' NS,', ' NS ', ' NB,', ' NB ',
                               'ONTARIO', 'BRITISH COLUMBIA', 'ALBERTA', 'QUEBEC', 'MANITOBA',
                               'TORONTO', 'VANCOUVER', 'CALGARY', 'OTTAWA', 'MONTREAL', 'CANADA'];
    for (const province of canadianProvinces) {
      if (addrUpper.includes(province)) return 'CA';
    }
    return 'US';
  };

  const handleLookup = async () => {
    if (!isPro) {
      toast.error('Auto-Fill is a Pro feature. Please upgrade to access live market data.');
      return;
    }

    if (!address.trim()) {
      toast.error('Please enter a property address');
      return;
    }

    setLoading(true);
    try {
      const country = detectCountry(address);
      let response;

      if (country === 'CA') {
        response = await axios.post(`${API}/market-data/canada/realtor-lookup`, {
          address: address
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        response = await axios.post(`${API}/market-data/lookup`, {
          address: address,
          bedrooms: 3,
          bathrooms: 2
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      setMarketData(response.data);
      
      // Call the callback with fetched data
      if (onDataFetched) {
        onDataFetched(response.data);
      }

      toast.success('Property data loaded!');
    } catch (error) {
      console.error('Market data lookup failed:', error);
      if (error.response?.status === 403) {
        toast.error('Auto-Fill is a Pro feature. Please upgrade to access.');
      } else {
        toast.error('Could not find data for this address');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(value);
  };

  // Show upgrade prompt for Light users
  if (!isPro) {
    return (
      <div className="glass-card rounded-xl p-4 border-amber-500/30 bg-gradient-to-r from-amber-500/5 to-transparent">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/20">
              <Lock className="h-5 w-5 text-amber-500" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-white">Auto-Fill Magic</span>
                <span className="px-2 py-0.5 text-xs font-bold bg-amber-500/20 text-amber-500 rounded">PRO</span>
              </div>
              <p className="text-xs text-slate-400 mt-0.5">
                Get instant market data, rent estimates, and comparable sales
              </p>
            </div>
          </div>
          <Button
            onClick={() => navigate('/upgrade')}
            className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold text-sm"
          >
            <Crown className="h-4 w-4 mr-1" />
            Upgrade
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Address Input with Magic Button */}
      <div className="glass-card rounded-xl p-4 border-amber-500/30 bg-gradient-to-r from-amber-500/5 to-transparent">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-5 w-5 text-amber-500" />
          <span className="text-sm font-semibold text-amber-500">Auto-Fill Magic</span>
          <span className="px-2 py-0.5 text-xs font-bold bg-amber-500/20 text-amber-500 rounded">PRO</span>
          <span className="px-2 py-0.5 text-xs font-bold bg-blue-500/20 text-blue-400 rounded ml-1">🇺🇸 US</span>
          <span className="px-2 py-0.5 text-xs font-bold bg-red-500/20 text-red-400 rounded">🇨🇦 CA</span>
        </div>
        
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Enter US or Canadian address"
              className="pl-10 bg-slate-900 border-slate-700 text-white"
              onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
            />
          </div>
          <Button
            onClick={handleLookup}
            disabled={loading || !address.trim()}
            className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold whitespace-nowrap"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Looking up...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Auto-Fill
              </>
            )}
          </Button>
        </div>
        
        <p className="text-xs text-slate-500 mt-2">
          Instantly get property value, market rent, and neighborhood data for US and Canadian properties
        </p>
      </div>

      {/* Market Data Results */}
      {marketData && (
        <div className="glass-card rounded-xl p-4 animate-fade-in">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-white flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-emerald-500" />
              Market Data Loaded
            </h3>
            {marketData.cached && (
              <span className="text-xs text-slate-500 bg-slate-800 px-2 py-1 rounded">
                Cached
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Estimated Value */}
            <div className="p-3 bg-slate-800/50 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="h-4 w-4 text-emerald-500" />
                <span className="text-xs text-slate-400">Est. Value</span>
              </div>
              <div className="text-lg font-bold text-emerald-500">
                {formatCurrency(marketData.estimated_value)}
              </div>
              {marketData.value_low && marketData.value_high && (
                <div className="text-xs text-slate-500">
                  {formatCurrency(marketData.value_low)} - {formatCurrency(marketData.value_high)}
                </div>
              )}
            </div>

            {/* Market Rent */}
            <div className="p-3 bg-slate-800/50 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <Home className="h-4 w-4 text-cyan-500" />
                <span className="text-xs text-slate-400">Market Rent</span>
              </div>
              <div className="text-lg font-bold text-cyan-500">
                {formatCurrency(marketData.estimated_rent)}/mo
              </div>
              {marketData.rent_low && marketData.rent_high && (
                <div className="text-xs text-slate-500">
                  {formatCurrency(marketData.rent_low)} - {formatCurrency(marketData.rent_high)}
                </div>
              )}
            </div>

            {/* Neighborhood Cap Rate */}
            <div className="p-3 bg-slate-800/50 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="h-4 w-4 text-amber-500" />
                <span className="text-xs text-slate-400">Nbhd Cap Rate</span>
              </div>
              <div className="text-lg font-bold text-amber-500">
                {marketData.neighborhood_cap_rate ? `${marketData.neighborhood_cap_rate}%` : 'N/A'}
              </div>
            </div>

            {/* Days on Market */}
            <div className="p-3 bg-slate-800/50 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <Calendar className="h-4 w-4 text-violet-500" />
                <span className="text-xs text-slate-400">Avg Days on Mkt</span>
              </div>
              <div className="text-lg font-bold text-violet-500">
                {marketData.days_on_market || 'N/A'}
              </div>
            </div>
          </div>

          {/* Property Details */}
          {(marketData.bedrooms || marketData.square_feet || marketData.year_built) && (
            <div className="mt-4 pt-4 border-t border-slate-700">
              <div className="flex flex-wrap gap-4 text-sm text-slate-400">
                {marketData.bedrooms && (
                  <span>{marketData.bedrooms} bed</span>
                )}
                {marketData.bathrooms && (
                  <span>{marketData.bathrooms} bath</span>
                )}
                {marketData.square_feet && (
                  <span>{marketData.square_feet.toLocaleString()} sqft</span>
                )}
                {marketData.year_built && (
                  <span>Built {marketData.year_built}</span>
                )}
                {marketData.property_type && (
                  <span>{marketData.property_type}</span>
                )}
              </div>
            </div>
          )}

          {/* Comparables */}
          {marketData.comparables && marketData.comparables.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-700">
              <h4 className="text-xs font-semibold text-slate-400 mb-2">
                Recent Comps ({marketData.comparables.length} properties)
              </h4>
              <div className="space-y-2">
                {marketData.comparables.slice(0, 3).map((comp, i) => (
                  <div key={i} className="flex justify-between text-xs">
                    <span className="text-slate-400 truncate flex-1">{comp.address}</span>
                    <span className="text-emerald-500 ml-2">
                      {formatCurrency(comp.sale_price)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
